/**
 * CRM · Pipeline (Kanban) — tablero de leads y clientes por FASE. Las fases son
 * configurables por organización. Se arrastra una tarjeta entre columnas para
 * cambiar su fase. Gated por crm:read (ver) / crm:manage (mover, editar fases).
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Cog6ToothIcon, PlusIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import { ContactModalHost, type ContactModalTarget } from './CrmContacts';
import { crmApi, type CrmStage, type CrmPipelineCard } from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';

const cardClass = 'bg-white rounded-lg border border-gray-200 shadow-sm';
const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

const cardName = (c: CrmPipelineCard) =>
  c.kind === 'company' ? (c.companyName || '(empresa)') : ([c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || '(sin nombre)');

export default function CrmPipeline() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.CRM_MANAGE));

  const [modalTarget, setModalTarget] = useState<ContactModalTarget | null>(null);
  const openFicha = (id: string) => setModalTarget({ mode: 'id', id });

  const [stages, setStages] = useState<CrmStage[]>([]);
  const [cards, setCards] = useState<CrmPipelineCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await crmApi.getPipeline();
      setStages((res.data.stages || []).slice().sort((a: CrmStage, b: CrmStage) => a.order - b.order));
      setCards(res.data.cards || []);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status !== 403) toast.error('No se pudo cargar el pipeline');
      setStages([]); setCards([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const firstStageId = stages[0]?.id;
  const stageOf = (c: CrmPipelineCard) => c.stage || firstStageId;

  const moveTo = async (cardId: string, stageId: string) => {
    const card = cards.find((c) => c._id === cardId);
    if (!card || stageOf(card) === stageId) return;
    setCards((prev) => prev.map((c) => (c._id === cardId ? { ...c, stage: stageId } : c))); // optimista
    try { await crmApi.updateContact(cardId, { stage: stageId }); }
    catch { toast.error('No se pudo mover'); void load(); }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900">Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1 hidden sm:block">Leads y clientes por fase. Arrastra las tarjetas para cambiar de fase (en móvil, usa el selector). Doble clic o el icono ↗ abre la ficha.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setModalTarget({ mode: 'new', type: 'lead' })} className="inline-flex items-center gap-1.5 px-3 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700">
              <PlusIcon className="w-5 h-5" /> Lead
            </button>
            <button onClick={() => setModalTarget({ mode: 'new', type: 'customer' })} className="inline-flex items-center gap-1.5 px-3 py-2 border border-vida-300 text-vida-700 rounded-lg text-sm font-medium hover:bg-vida-50">
              <PlusIcon className="w-5 h-5" /> Cliente
            </button>
            <button onClick={() => setConfigOpen(true)} title="Configurar fases" className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Cog6ToothIcon className="w-5 h-5" /> <span className="hidden sm:inline">Fases</span>
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : (
        <div className="flex flex-col md:flex-row gap-3 items-stretch">
          {stages.map((st) => {
            const colCards = cards.filter((c) => stageOf(c) === st.id);
            return (
              <div key={st.id} className="w-full md:flex-1 md:min-w-0"
                onDragOver={(e) => { if (canManage) e.preventDefault(); }}
                onDrop={() => { if (canManage && dragId) { void moveTo(dragId, st.id); setDragId(null); } }}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: st.color || '#94a3b8' }} />
                    <span className="text-sm font-semibold text-gray-800 truncate">{st.name}</span>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{colCards.length}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 min-h-[80px] space-y-2">
                  {colCards.map((c) => (
                    <div key={c._id} draggable={canManage} onDragStart={() => setDragId(c._id)}
                      onDoubleClick={() => openFicha(c._id)} title="Doble clic para abrir la ficha"
                      className={`${cardClass} p-2.5 ${canManage ? 'md:cursor-grab md:active:cursor-grabbing' : ''}`}>
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.kind === 'company' ? '🏢 ' : ''}{cardName(c)}</p>
                        <button onClick={(e) => { e.stopPropagation(); openFicha(c._id); }} title="Abrir ficha"
                          className="p-0.5 text-gray-300 hover:text-vida-600 flex-shrink-0"><ArrowTopRightOnSquareIcon className="w-4 h-4" /></button>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.type === 'customer' ? 'bg-vida-100 text-vida-700' : 'bg-gray-100 text-gray-600'}`}>{c.type === 'customer' ? 'Cliente' : 'Lead'}</span>
                        {c.email && <span className="text-[11px] text-gray-400 truncate">{c.email}</span>}
                      </div>
                      {/* En móvil (sin drag fiable) se mueve con el selector. */}
                      {canManage && (
                        <select value={stageOf(c) || ''} onChange={(e) => void moveTo(c._id, e.target.value)}
                          className="md:hidden mt-2 w-full text-xs border-gray-200 rounded py-1 text-gray-500 focus:ring-vida-500 focus:border-vida-500">
                          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                  ))}
                  {colCards.length === 0 && <p className="text-xs text-gray-300 text-center py-4">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {configOpen && (
        <StagesConfig stages={stages} onClose={() => setConfigOpen(false)} onSaved={(s) => { setStages(s); setConfigOpen(false); }} />
      )}

      {modalTarget && (
        <ContactModalHost
          target={modalTarget}
          hideAi
          onClose={() => setModalTarget(null)}
          onSaved={() => { setModalTarget(null); void load(); }}
        />
      )}
    </div>
  );
}

function StagesConfig({ stages, onClose, onSaved }: { stages: CrmStage[]; onClose: () => void; onSaved: (s: CrmStage[]) => void }) {
  const [list, setList] = useState<CrmStage[]>(stages.map((s) => ({ ...s })));
  const [busy, setBusy] = useState(false);

  const slug = (name: string) =>
    name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `fase-${Date.now()}`;
  const update = (i: number, patch: Partial<CrmStage>) => setList((l) => l.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const move = (i: number, dir: -1 | 1) => setList((l) => {
    const j = i + dir; if (j < 0 || j >= l.length) return l;
    const copy = l.slice(); [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });
  const add = () => setList((l) => [...l, { id: `fase-${l.length + 1}-${Date.now().toString(36)}`, name: 'Nueva fase', color: '#94a3b8', order: l.length }]);
  const del = (i: number) => setList((l) => l.filter((_, idx) => idx !== i));

  const save = async () => {
    if (list.length === 0) { toast.error('Debe haber al menos una fase'); return; }
    setBusy(true);
    try {
      const normalized = list.map((s, idx) => ({ id: s.id || slug(s.name), name: s.name.trim() || 'Fase', color: s.color, order: idx }));
      const res = await crmApi.saveStages(normalized);
      toast.success('Fases guardadas');
      onSaved(res.data.stages);
    } catch { toast.error('No se pudieron guardar'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`${cardClass} w-full max-w-md p-6 max-h-[90vh] overflow-y-auto`}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configurar fases del pipeline</h2>
        <div className="space-y-2">
          {list.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="color" value={s.color || '#94a3b8'} onChange={(e) => update(i, { color: e.target.value })} className="w-8 h-8 rounded border border-gray-300 p-0.5" />
              <input className={inputClass} value={s.name} onChange={(e) => update(i, { name: e.target.value })} />
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUpIcon className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDownIcon className="w-4 h-4" /></button>
              <button onClick={() => del(i)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <button onClick={add} className="mt-3 inline-flex items-center gap-1 text-sm text-vida-700 hover:bg-vida-50 px-2 py-1 rounded"><PlusIcon className="w-4 h-4" /> Añadir fase</button>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}
